import * as Dialog from "@radix-ui/react-dialog";
import { AnimatePresence, motion, useDragControls, useReducedMotion, type PanInfo } from "motion/react";
import { useEffect, useId, useRef, type ReactNode } from "react";
import { FEDER, UEBERBLENDUNG } from "./federn";
import { federMotion } from "../buehne/feder";
import { istEingabe } from "./tastatur";
import { useBreit } from "./useBreit";
import { useWeltAktiv } from "../welt/WeltWurzel";

type SheetProps = {
  offen: boolean;
  onSchliessen: () => void;
  titel: string;
  untertitel?: string;
  /** Links im Kopf (Standard „Abbrechen“), rechts optional „Fertig“ o. ae. */
  rechts?: ReactNode;
  breit?: boolean;
  /** D-089 (W51): waehrend des Sicherns schliesst nichts das Blatt; „Abbrechen“ bleibt erreichbar und nennt den Grund */
  gesperrt?: boolean;
  /** R1 Beta-W: Grund neben „Abbrechen“, solange gesperrt (Vorgabe „Wird gesichert …“; das Blatt „Rückmeldung geben“ sendet) */
  sperrText?: string;
  /** W82 Web-5c (K15): Fokusziel nach dem Schliessen statt des Ausloesers (etwa die neu angelegte Zeile); null: wie bisher */
  fokusNachher?: () => HTMLElement | null;
  /** W82 Web-5h-N2 a5 (Frage 73 = A, D-684): Hinweisblatt ohne „Abbrechen“ oben links; Escape, Wischen und der Schleier
   *  schliessen weiter, die Sperrzeile bleibt, der Kopf bleibt dreispaltig */
  ohneAbbrechen?: boolean;
  /** W82 Web-5h-N2b-2 (b-HB1, D-701): nur der eigene Knopf schließt (Beta-Begrüßung, „Verstanden“); Escape, Klick daneben und
   *  Wischen lassen das Blatt offen. Kein Sichervorgang: nichts im Blatt wird gesperrt. Standard aus, jedes andere Blatt bleibt */
  nurMitKnopf?: boolean;
  children: ReactNode;
};

/** Sheet (Handoff Schiefer): mobil von unten und ziehbar (Radius 14, Schatten sheet, Overlay 45 %), ab
 *  744 px zentrierter Dialog 480 (breit 900), Radius 14. Radix Dialog liefert Fokusfalle, Esc,
 *  aria-labelledby und Fokus zurueck. Panel rechts (360) folgt mit dem Dokument-Umbau (W22b). */
export function Sheet({ offen, onSchliessen, titel, untertitel, rechts, breit, gesperrt = false, sperrText = "Wird gesichert …", fokusNachher, ohneAbbrechen = false, nurMitKnopf = false, children }: SheetProps) {
  const desktop = useBreit();
  const ruhig = useReducedMotion();
  // W82 Teil 3: unter einer WeltWurzel trägt das Portal selbst data-welt (es hängt außerhalb der Wurzel) und die W82-Gestalt
  // (welt.css „Blatt“): deckende Fläche im Verlauf des Grunds, Ecke 26, Griff, Titel in Bricolage, Schleier, keine Unschärfe
  const welt = useWeltAktiv();
  const ziehen = useDragControls();
  const blatt = useRef<HTMLDivElement>(null);
  const schleier = useRef<HTMLDivElement>(null);
  const zu = useRef<HTMLButtonElement>(null);
  // W82 Web-5c-N1 (K38): ohne visualViewport (aeltere Browser) steht das Blatt wie bisher am Fensterboden
  const sicht = !desktop && typeof window !== "undefined" && Boolean(window.visualViewport);
  // bis das Blatt eingefedert ist, holt ein Fokus (autoFocus) das Feld nicht ins Bild: es kommt ja noch von unten
  const steht = useRef(false);
  const grund = useId();
  // Ausloeser merken: ohne Dialog.Trigger findet Radix ihn nicht, der Fokus landete auf body (F7)
  const ausloeser = useRef<HTMLElement | null>(null);
  useEffect(() => {
    if (offen && document.activeElement instanceof HTMLElement) ausloeser.current = document.activeElement;
  }, [offen]);

  // H-R3-2: ein gesperrter Knopf gibt den Fokus an body ab, Radix holt ihn nicht zurueck (focus-scope :50, :58). Je
  // Bildtakt: liegt der Fokus ausserhalb jedes Dialogs, nachdem er schon im Blatt war, kommt er auf „Abbrechen“. W82 Web-6a-N1
  // (WN-20): ein Menü aus dem Blatt (Auswahl der Akte bei großer Schrift, WahlFeld.tsx) zählt wie ein Dialog. Vor dem
  // ersten Fokus im Blatt nicht: beim Oeffnen setzt Radix ihn selbst (onOpenAutoFocus, autoFocus der Felder).
  useEffect(() => {
    if (!offen) return;
    let takt = 0;
    let warDrin = false;
    let zuletzt: Element | null = null;
    const halten = () => {
      const jetzt = document.activeElement;
      const imBlatt = Boolean(jetzt && blatt.current?.contains(jetzt));
      const inDialog = Boolean(jetzt && jetzt !== document.body && jetzt.closest('[role="dialog"], [role="menu"]'));
      // R2 W82-Web-5c: das Datumsfeld gibt den Fokus nach der Wahl selbst ab (Feld.tsx, data-fokus-frei); er bleibt dann frei,
      // statt auf „Abbrechen“ zu springen, wo ein Enter das Blatt verwarf
      const frei = jetzt === document.body && Boolean(zuletzt?.isConnected && zuletzt.hasAttribute("data-fokus-frei"));
      if (imBlatt) warDrin = true;
      // ohne „Abbrechen“ (Hinweisblatt) kommt er auf das Blatt selbst (Radix gibt ihm tabIndex -1)
      else if (warDrin && !inDialog && !frei) (zu.current ?? blatt.current)?.focus({ preventScroll: true });
      takt = requestAnimationFrame(halten);
    };
    // das zuletzt fokussierte Element im Blatt, je Fokus (Wahl und Fokus koennen im selben Bildtakt liegen)
    const merken = (e: FocusEvent) => {
      if (!(e.target instanceof Element) || !blatt.current?.contains(e.target)) return;
      // W82 Web-5h2 (K46): fallen im Blatt Knoten weg, während der Fokus auf body liegt, holt Radix ihn auf das Blatt selbst
      // (focus-scope `handleMutations`); so nach „Briefdatum“, dessen Hinweis sich nach der Wahl ändert. Hat das Datumsfeld ihn
      // eben abgegeben, bleibt er frei auf body wie bei „Ab“ und „Frist bis“
      if (e.target === blatt.current && zuletzt?.isConnected && zuletzt.hasAttribute("data-fokus-frei")) {
        blatt.current.blur();
        return;
      }
      zuletzt = e.target;
    };
    document.addEventListener("focusin", merken);
    takt = requestAnimationFrame(halten);
    return () => {
      cancelAnimationFrame(takt);
      document.removeEventListener("focusin", merken);
    };
  }, [offen]);
  // W82 Web-5c-N1 (K38): Safari verkleinert und verschiebt bei Tastatur und Datumswaehler nur den sichtbaren Ausschnitt; das
  // feste Blatt hing am Fenster und sass danach verschoben (unten am Rand, „Abbrechen“ links abgeschnitten, oben ohne
  // Abdunkelung). Je `resize` und `scroll` des Ausschnitts: das Blatt steht in ihm (--sicht-* am Blatt, bausteine.css und
  // welt.css), die Abdunkelung reicht ueber Fenster und Ausschnitt; schliessen Tastatur oder Waehler, ist es wieder unten.
  // Ein Feld mit Fokus bleibt im Blick, auch wenn die Tastatur aufgeht.
  useEffect(() => {
    const vv = window.visualViewport;
    if (!offen || desktop || !vv) return;
    const imBlick = (el: Element | null) => {
      if (steht.current && istEingabe(el) && blatt.current?.contains(el)) el.scrollIntoView?.({ block: "nearest" });
    };
    let takt = 0;
    // W82 Web-5h2 (HW21): schließt die Tastatur, weil der Fokus im Blatt weiterwandert (Titel zum Datumsfeld, dessen Wähler
    // erscheint), bleibt die Oberkante des Blatts stehen: es steht weiter am unteren Rand, nur der Freiraum unten wächst
    // (`--sicht-frei`, bausteine.css). Gehalten wird ab dem Abstand `halt`, bis die Tastatur wieder aufgeht, der Fokus das Blatt
    // verlässt oder es schließt; ein Datumsfeld, das den Fokus nach der Wahl selbst abgibt (`data-fokus-frei`), hält weiter
    let vorher = 0;
    let halt: number | null = null;
    // gehaltene Oberkante (px im Fenster) und der Freiraum, der sie hält; nachgeführt, weil der Abstand bei offener Tastatur
    // (8 px über dem Ausschnitt) und danach (bündig) verschieden ist
    let haltOben: number | null = null;
    let frei = 0;
    let korrektur = 0;
    // Oberkante im zuletzt ruhigen Stand (nach dem Takt gemessen): beim Wachsen des Ausschnitts hat die Tastatur-Regel
    // (`data-tastatur`, tastatur.ts) das Blatt womöglich schon verschoben, bevor dieser Hörer läuft
    let obenStand: number | null = null;
    // R1 W82-Web-5h2: zuletzt fokussiertes Element im Blatt (ohne das Blatt selbst, das Radix nach einer Wahl kurz fokussiert)
    let letztes: Element | null = null;
    // gehalten wird nur, solange ein Feld im Blatt den Fokus hat (Text, Datum, Auswahl) oder das zuletzt fokussierte Datumsfeld ihn
    // nach der Wahl selbst abgab; ein Knopf hält nicht (nach „Fertig“ legt die Fokuswache den Fokus auf „Abbrechen“, die Haltung
    // blieb sonst mit leerem Freiraum unten stehen); ein alter Merker an einem früher gewählten Datumsfeld zählt nicht
    const imBlattFokus = () => {
      const a = document.activeElement;
      // W82 Web-5h-N2b (b1): Kalender-Knopf und Monatsblatt des Datumsfelds halten wie ein Feld (`data-feld-halt`, Feld.tsx)
      if (a && a !== document.body) return Boolean(blatt.current?.contains(a)) && (istEingabe(a) || a instanceof HTMLSelectElement || a.hasAttribute("data-feld-halt"));
      return Boolean(letztes?.isConnected && letztes.hasAttribute("data-fokus-frei") && blatt.current?.contains(letztes));
    };
    const setzen = () => {
      const breite = document.documentElement.clientWidth;
      const hoehe = window.innerHeight;
      const links = Math.round(vv.offsetLeft);
      const oben = Math.round(vv.offsetTop);
      const rechts = Math.round(breite - vv.offsetLeft - vv.width);
      const unten = Math.max(0, Math.round(hoehe - vv.offsetTop - vv.height));
      const b = blatt.current;
      if (unten > vorher || !imBlattFokus()) {
        halt = null;
        haltOben = null;
        frei = 0;
      } else if (unten < vorher && halt === null && steht.current) {
        halt = vorher;
        haltOben = obenStand;
      }
      if (halt !== null) frei = Math.max(frei, halt - unten);
      vorher = unten;
      if (b) {
        b.style.setProperty("--sicht-links", `${Math.max(0, links)}px`);
        b.style.setProperty("--sicht-rechts", `${Math.max(0, rechts)}px`);
        b.style.setProperty("--sicht-unten", `${unten}px`);
        b.style.setProperty("--sicht-h", `${Math.round(vv.height)}px`);
        b.style.setProperty("--sicht-frei", `${frei}px`);
        cancelAnimationFrame(korrektur);
        korrektur = requestAnimationFrame(() => {
          const jetzt = b.getBoundingClientRect().top;
          if (haltOben === null) {
            obenStand = jetzt;
            return;
          }
          const d = Math.round(jetzt - haltOben);
          if (Math.abs(d) < 1) return;
          frei = Math.max(0, frei + d);
          b.style.setProperty("--sicht-frei", `${frei}px`);
        });
      }
      const s = schleier.current;
      if (s) {
        s.style.top = `${Math.min(0, oben)}px`;
        s.style.left = `${Math.min(0, links)}px`;
        s.style.right = `${Math.min(0, rechts)}px`;
        s.style.bottom = `${Math.min(0, unten)}px`;
      }
    };
    const geaendert = () => {
      setzen();
      cancelAnimationFrame(takt);
      takt = requestAnimationFrame(() => imBlick(document.activeElement));
    };
    const fokus = (e: FocusEvent) => {
      const el = e.target as Element;
      if (el !== blatt.current && blatt.current?.contains(el)) letztes = el;
      imBlick(el);
      // R1 W82-Web-5h2: wandert der Fokus auf einen Knopf, löst die Haltung sofort (nicht erst mit dem nächsten Ausschnitt)
      if (halt !== null && el !== blatt.current && !imBlattFokus()) setzen();
    };
    setzen();
    // das Portal haengt erst nach dem ersten Takt im DOM
    takt = requestAnimationFrame(setzen);
    vv.addEventListener("resize", geaendert);
    vv.addEventListener("scroll", geaendert);
    document.addEventListener("focusin", fokus);
    return () => {
      cancelAnimationFrame(takt);
      cancelAnimationFrame(korrektur);
      vv.removeEventListener("resize", geaendert);
      vv.removeEventListener("scroll", geaendert);
      document.removeEventListener("focusin", fokus);
    };
  }, [offen, desktop]);
  useEffect(() => {
    if (!offen) steht.current = false;
  }, [offen]);
  // F2 = A: Neuladen oder Schliessen des Tabs fragt nach, solange gesichert wird
  useEffect(() => {
    if (!gesperrt) return;
    const fragen = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener("beforeunload", fragen);
    return () => window.removeEventListener("beforeunload", fragen);
  }, [gesperrt]);
  const schliessen = () => {
    if (!gesperrt) onSchliessen();
  };
  // b-HB1: Wischen gibt es nur, wenn ein passiver Weg schließen darf
  const ziehbar = !desktop && !ruhig && !gesperrt && !nurMitKnopf;

  function losgelassen(_: unknown, info: PanInfo) {
    // Schliessen ab 30 % der Sheet-Hoehe oder schneller als 500 px/s (8.8)
    const hoehe = blatt.current?.offsetHeight ?? window.innerHeight;
    if (info.offset.y > hoehe * 0.3 || info.velocity.y > 500) schliessen();
  }

  // W82 Design-Web-1 (Teil B d): mobil steigt das Blatt mit der Feder „blatt“ (Dämpfung 0,84, Antwort 440 ms) und sinkt mit
  // „ruhe“ (1,0 / 420 ms) zurück; am Desktop bleibt die Zentrierung, der Maßstab folgt der Feder „dialog“ (0,8 / 500 ms), die
  // Deckkraft 220 ms expo. Ruhige Fassung: kritisch gedämpft. Ziehen, Wurf, Schließen, Sperre und Kette bleiben Bestand
  const fassungRuhig = typeof document !== "undefined" && document.documentElement.dataset.fassung === "ruhig";
  const bewegung = ruhig
    ? { initial: { opacity: 0 }, animate: { opacity: 1 }, exit: { opacity: 0, pointerEvents: "none" as const }, transition: UEBERBLENDUNG }
    : desktop
      ? {
          initial: { opacity: 0, scale: 0.96 },
          animate: { opacity: 1, scale: 1, transition: { ...federMotion("dialog", fassungRuhig), opacity: { duration: 0.22, ease: [0.16, 1, 0.3, 1] as const } } },
          exit: { opacity: 0, scale: 0.96, pointerEvents: "none" as const, transition: FEDER.sheetZu },
        }
      : {
          initial: { y: "100%" },
          animate: { y: 0, transition: federMotion("blatt", fassungRuhig) },
          // Beim Ausblenden nimmt das Sheet keine Klicks mehr an (Verifier W4, B3)
          exit: { y: "100%", pointerEvents: "none" as const, transition: federMotion("ruhe", fassungRuhig) },
        };

  return (
    <Dialog.Root open={offen} onOpenChange={(o) => !o && schliessen()}>
      <AnimatePresence>
        {offen && (
          <Dialog.Portal forceMount>
            <Dialog.Overlay asChild forceMount>
              <motion.div
                ref={schleier}
                data-welt={welt ? "" : undefined}
                className={`fixed inset-0 z-40 bg-pg-overlay ${welt ? "blatt-schleier" : ""}`}
                // R1 W82 Web-5h-N2b-2 (b-HB1): ein Tipp daneben schließt nicht und nimmt dem Knopf auch nicht den Fokus (sonst holte die
                // Fokuswache ihn ohne „Abbrechen“ auf das Blatt selbst, mit Rahmen um die ganze Fläche)
                onMouseDown={nurMitKnopf ? (e) => e.preventDefault() : undefined}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={UEBERBLENDUNG}
              />
            </Dialog.Overlay>
            <Dialog.Content
              asChild
              forceMount
              {...(untertitel ? {} : { "aria-describedby": undefined })}
              // W82 Web-5h-N2b (b1): Escape in einem offenen Monatsblatt (`data-esc-eigen`, Feld.tsx) schließt nur dieses, nie das Blatt
              onEscapeKeyDown={(e) => (gesperrt || nurMitKnopf || (e.target instanceof Element && e.target.closest("[data-esc-eigen]"))) && e.preventDefault()}
              onInteractOutside={(e) => (gesperrt || nurMitKnopf) && e.preventDefault()}
              onCloseAutoFocus={(e) => {
                e.preventDefault();
                // Ist der Ausloeser weg (etwa nach dem Ablegen) oder gab es keinen (W73d, D-314 H2: das Blatt oeffnete von selbst,
                // gemerkt war `body`), auf die Ueberschrift der Seite
                const a = ausloeser.current;
                const nachher = fokusNachher?.();
                const ziel = nachher?.isConnected ? nachher : a?.isConnected && a !== document.body ? a : document.querySelector<HTMLElement>("main h1");
                ziel?.focus({ preventScroll: true });
              }}
            >
              <motion.div
                ref={blatt}
                data-welt={welt ? "" : undefined}
                data-sicht={sicht ? "" : undefined}
                {...bewegung}
                onAnimationComplete={() => {
                  // eingefedert: ein Feld, das schon beim Oeffnen den Fokus bekam, kommt jetzt ins Bild
                  if (!offen || steht.current) return;
                  steht.current = true;
                  const f = document.activeElement;
                  if (sicht && istEingabe(f) && blatt.current?.contains(f)) f.scrollIntoView?.({ block: "nearest" });
                }}
                drag={ziehbar ? "y" : false}
                dragListener={false}
                dragControls={ziehen}
                dragConstraints={{ top: 0, bottom: 0 }}
                dragElastic={{ top: 0, bottom: 0.6 }}
                onDragEnd={losgelassen}
                className={`${
                  desktop
                    ? `fixed top-1/2 left-1/2 z-50 flex max-h-(--sheet-max-h) max-w-(--dialog-max-w) flex-col overflow-hidden rounded-(--r-dialog) bg-pg-bg shadow-sheet ${breit ? "w-(--w-detail)" : "w-(--w-dialog)"}`
                    : "blatt-mobil fixed inset-x-2 bottom-2 z-50 flex max-h-(--sheet-max-h-mobil) flex-col overflow-hidden rounded-sheet bg-pg-bg shadow-sheet"
                }${welt ? " blatt-welt" : ""}`}
                style={desktop ? { x: "-50%", y: "-50%" } : undefined}
              >
                <div
                  className="sheet-kopf-huelle sticky top-0 z-10 touch-none bg-pg-bg px-4 pt-2 pb-2"
                  onPointerDown={(e) => ziehbar && ziehen.start(e)}
                >
                  {!desktop && (
                    <div aria-hidden className="blatt-griff mx-auto mb-1 h-(--grabber-h) w-(--grabber-w) rounded-pill bg-pg-border-strong" />
                  )}
                  <div className="sheet-kopf grid grid-cols-[1fr_auto_1fr] items-center gap-x-4">
                    {/* Gesperrt: Name bleibt „Abbrechen“ (Tests nennen ihn so, iOS und Android auch), aria-disabled statt
                        disabled, damit der Fokus bleiben kann; der Klick schliesst nicht (Radix achtet auf defaultPrevented) */}
                    <div className="justify-self-start">
                      {!ohneAbbrechen && (
                        <Dialog.Close
                          ref={zu}
                          aria-disabled={gesperrt || undefined}
                          aria-describedby={gesperrt ? grund : undefined}
                          onClick={(e) => gesperrt && e.preventDefault()}
                          className={`sheet-abbrechen t-body min-h-(--touch) ${gesperrt ? "cursor-default text-pg-text-3" : "text-pg-accent-text hover:opacity-70"}`}
                        >
                          Abbrechen
                        </Dialog.Close>
                      )}
                      {gesperrt && (
                        <p id={grund} className="t-caption-1 text-balance text-pg-text-2">
                          {sperrText}
                        </p>
                      )}
                    </div>
                    <div className="sheet-titel min-w-0 text-center">
                      {/* W77: ein langes Wort im Titel („Nutzungsstatistik“) trennt bei 320 px und 200 %, statt seitlich zu ragen */}
                      <Dialog.Title className="t-headline hyphens-auto">{titel}</Dialog.Title>
                      {untertitel && (
                        <Dialog.Description className="t-footnote wrap-anywhere text-pg-text-2">{untertitel}</Dialog.Description>
                      )}
                    </div>
                    <div className="sheet-rechts justify-self-end">{rechts}</div>
                  </div>
                </div>
                {/* W73d (HB1): nur der Inhalt rollt; der Kopf bleibt bei offener Tastatur im Ausschnitt (bausteine.css, .blatt-mobil) */}
                <div className="overflow-y-auto overscroll-contain px-4 pb-6">{children}</div>
              </motion.div>
            </Dialog.Content>
          </Dialog.Portal>
        )}
      </AnimatePresence>
    </Dialog.Root>
  );
}
